import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'

import colours from 'functions/colours'

import LoadingBubbles from 'components/LoadingBubbles'

import styles from './styles.scss'

const renderLoading = (color, ghost) => {
  const loadingColor = ghost ? colours[color] : 'white'

  return <LoadingBubbles color={loadingColor} />
}

const buttonClass = (props) => {
  const {
    borderless, className, color, contentDirection, dashed, disabled, loading, round, size, ghost,
  } = props

  return classnames(
    styles.root,
    styles[size],
    styles[color],
    styles[contentDirection],
    borderless ? styles.borderless : null,
    className,
    dashed ? styles.dashed : null,
    ghost ? styles.ghost : null,
    disabled ? styles.disabled : null,
    loading ? styles.loading : null,
    round ? styles.round : null,
  )
}

buttonClass.propTypes = {
  borderless: PropTypes.bool,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    'black',
    'blue',
    'blueLightBorder',
    'darkGrey',
    'darkLightBorder',
    'green',
    'grey',
    'hoverGrey',
    'lightGrey',
    'orange',
    'red',
    'white',
    'whiteGrey',
  ]),
  contentDirection: PropTypes.oneOf(['column', 'row']),
  dashed: PropTypes.bool,
  disabled: PropTypes.bool,
  ghost: PropTypes.bool,
  round: PropTypes.bool,
  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
}

const BaseButton = (props) => {
  const {
    color,
    dataTestId,
    download,
    ghost,
    hideText,
    href,
    icon,
    id,
    loading,
    onClick,
    preventDefault,
    stopPropagation,
    style,
    target,
    text,
    wrapperTag,
  } = props

  const ElementTag = wrapperTag || 'a'
  const rel = target === '_blank' ? 'noopener noreferrer' : ''

  const buttonRef = useRef()

  const stopPropagationFn = (e) => {
    if (onClick) onClick()

    if (preventDefault) e.preventDefault()
    if (stopPropagation) e.stopPropagation()
  }

  useEffect(() => {
    if ((stopPropagation || preventDefault) && buttonRef.current){
      buttonRef.current.addEventListener('click', stopPropagationFn, true)
    }

    return () => {
      if (buttonRef.current){
        buttonRef.current.removeEventListener('click', stopPropagationFn, true)
      }
    }
  }, [buttonRef.current])

  return (
    <ElementTag
      className={buttonClass(props)}
      data-test-id={dataTestId}
      download={download}
      href={href}
      id={id}
      onClick={onClick}
      style={style}
      tabIndex="0"
      target={target}
      ref={buttonRef}
      rel={rel}
    >
      {icon && !loading && <span className={styles.icon}>{icon}</span>}
      {!hideText && text && (
        <span className={icon ? classnames(styles.text, styles.icon) : styles.text}>
          {loading ? renderLoading(color, ghost) : text}
        </span>
      )}
    </ElementTag>
  )
}

BaseButton.propTypes = {
  color: PropTypes.oneOf([
    'black',
    'blue',
    'blueLightBorder',
    'darkGrey',
    'darkLightBorder',
    'green',
    'grey',
    'hoverGrey',
    'lightGrey',
    'orange',
    'red',
    'white',
    'whiteGrey',
  ]),
  dataTestId: PropTypes.string,
  download: PropTypes.bool,
  ghost: PropTypes.bool,
  hideText: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
  id: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  preventDefault: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  style: PropTypes.object,
  target: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  wrapperTag: PropTypes.string,
}

const ButtonWithDropdown = (props) => {
  const { children } = props

  const [showOptions, toggleShowOptions] = useState(false)

  return (
    <div className={styles.buttonHolder}>
      <BaseButton {...props} />
      <a tabIndex="0" className={styles.dropdownToggle} onClick={() => toggleShowOptions(!showOptions)}>
        <FontAwesomeIcon icon={faCaretDown} />
      </a>
      {showOptions && <ul className={styles.dropdown}>{children}</ul>}
    </div>
  )
}

ButtonWithDropdown.propTypes = {
  children: PropTypes.node.isRequired,
}

const Button = (props) => {
  const { children } = props

  if (children){
    return <ButtonWithDropdown {...props}>{children}</ButtonWithDropdown>
  }

  return <BaseButton {...props} />
}

Button.propTypes = {
  children: PropTypes.node,
}

Button.defaultProps = {
  color: 'green',
  contentDirection: 'row',
  dashed: false,
  disabled: false,
  loading: false,
  ghost: false,
  round: false,
  size: 'large',
  target: '_self',
}

export default Button
